export const grammarTokens = (text) => text.replace(/[‘’]/g, "'").replace(/[‐‑‒–—−－]/g, '-').match(/[a-z0-9]+(?:['-][a-z0-9]+)*/gi) || [];
export const requiresSentenceGrammar = (text) => grammarTokens(text).length > 1 || (grammarTokens(text).length === 1 && /[.!?]\s*$/.test(text));
export const normalizeGrammarRole = (label) => label.trim().replace(/\s*[+＋]\s*/g, '＋').replace(/宾语补足语/g, '宾补').replace(/谓语动词|系动词/g, '谓语').replace(/不定式补足成分|补足语/g, '补语');
export function isGrammarRole(label) {
    if (/^(?:从句)?(?:形式)?主语＋谓语$/.test(normalizeGrammarRole(label)))
        return true;
    return /^(?:从句)?(?:主语|谓语|宾语|宾补|表语|定语|状语|补语|形式主语|真正主语|形式宾语|真正宾语|时间状语|地点状语|方式状语|原因状语|目的状语|条件状语|让步状语|结果状语|范围状语|程度状语|频率状语|比较状语|伴随状语|不定式宾语|直接宾语|间接宾语|同位语|呼语|独立语|插入语|连接成分)(?:从句)?(?:[（(][^（）()]{1,40}[）)])?$/.test(normalizeGrammarRole(label));
}
/** A contraction contains both pronoun and finite auxiliary; do not hide the predicate. */
export function normalizeContractedGrammar(structure) {
    return structure.flatMap(part => {
        if (!/^(主语|谓语|从句主语|从句谓语)$/.test(normalizeGrammarRole(part.label)))
            return [part];
        const tokens = grammarTokens(part.text);
        if (!tokens.some(word => /^(?:i|you|he|she|it|we|they)'(?:m|re|s|ve|ll|d)$/i.test(word)))
            return [part];
        return tokens.map(word => ({ ...part, text: word, label: /^(?:i|you|he|she|it|we|they)'(?:m|re|s|ve|ll|d)$/i.test(word) ? '主语＋谓语' : part.label }));
    });
}
/** Complete means exact token coverage AND syntactic roles, not POS or legacy placeholders. */
export function hasCompleteGrammar(sentence) {
    const parts = sentence.structure;
    if (!Array.isArray(parts) || !parts.length || parts.some(p => !p || typeof p.text !== 'string' || !p.text || typeof p.label !== 'string' || !isGrammarRole(p.label) || (p.text.trim().toLowerCase() === 'to' && p.label === '连接成分')))
        return false;
    const tokens = grammarTokens(sentence.english).map(w => w.toLowerCase());
    const actual = parts.flatMap(p => grammarTokens(p.text)).map(w => w.toLowerCase());
    return tokens.length > 0 && tokens.length === actual.length && tokens.every((t, i) => t === actual[i]);
}
/** Model output uses indexes, never fuzzy text matching that can shift a role onto the next word. */
export function validateGrammarSegments(english, value) {
    const tokens = grammarTokens(english);
    if (!Array.isArray(value) || !value.length || value.length > tokens.length * 2)
        throw new Error('语法分段为空或超出词数');
    const parts = [];
    for (const original of value) {
        const part = { ...original };
        const previous = parts[parts.length - 1];
        // The model may describe I'm as two functions at the exact same token.
        // Merge only this unambiguous contraction representation, never general overlaps.
        if (previous && previous.start === part.start && previous.end === part.end && part.end === part.start + 1 && /^(?:i|you|he|she|it|we|they)'(?:m|re|s|ve|ll|d)$/i.test(tokens[part.start] || '')) {
            const labels = [previous.role, part.role].map(role => typeof role === 'string' ? normalizeGrammarRole(role) : '');
            const subject = labels.find(role => /^(?:从句)?(?:形式)?主语$/.test(role));
            if (subject && labels.some(role => /^(?:从句)?谓语$/.test(role))) {
                previous.role = `${subject}＋谓语`;
                continue;
            }
        }
        if (part.role === '助动词' && Number.isInteger(part.start) && Number.isInteger(part.end) && part.end > part.start && tokens.slice(part.start, part.end).every(word => /^(do|does|did|am|is|are|was|were|be|been|being|have|has|had|can|could|may|might|will|would|shall|should|must)$/i.test(word)))
            part.role = '谓语';
        parts.push(part);
    }
    let cursor = 0;
    const structure = parts.map((part) => {
        if (!part || !Number.isInteger(part.start) || !Number.isInteger(part.end) || part.start !== cursor || part.end <= cursor || part.end > tokens.length || typeof part.role !== 'string' || !isGrammarRole(part.role))
            throw new Error('语法成分或词序范围不完整');
        cursor = part.end;
        return { label: normalizeGrammarRole(part.role), text: tokens.slice(part.start, part.end).join(' '), explain: typeof part.explain === 'string' ? part.explain.slice(0, 160) : '' };
    });
    if (cursor !== tokens.length || !hasCompleteGrammar({ english, structure }))
        throw new Error('仍有单词缺少语法成分');
    return normalizeContractedGrammar(structure);
}
